import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";

// Paid-first poem viewer. The user only reaches this page AFTER Stripe checkout
// is confirmed (via /poem/pending) or via bundle credit. The backend
// guarantees every record served here has is_paid=true.
export default function Poem() {
  const { poem_id } = useParams();
  const { confirm } = useConfirm();
  const [poem, setPoem] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tone, setTone] = useState("loving");
  const [scheme, setScheme] = useState("ABAB");

  useEffect(() => {
    if (!poem_id) return;
    api.get(`/poem/${poem_id}`).then(({ data }) => {
      setPoem(data);
      setDraft(data.text || "");
      setTone(data.tone || "loving");
      setScheme(data.scheme || "ABAB");
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Could not load poem.");
    }).finally(() => setLoading(false));
  }, [poem_id]);

  const save = async () => {
    if (!draft || draft.trim().length < 20) {
      toast.error("Poem is too short.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/poem/${poem_id}/edit`, { text: draft });
      setPoem(p => ({ ...p, text: draft, rhyme_score: data.rhyme_score }));
      toast.success(`Saved. Rhyme score: ${data.rhyme_score}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    const ok = await confirm({
      title: "Re-roll this poem?",
      message: `You've used ${poem.regenerations_used || 0} of 5 free re-rolls. A new version will replace the current text.`,
      confirmLabel: "Re-roll",
      cancelLabel: "Keep current",
    });
    if (!ok) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/poem/${poem_id}/regenerate`, { tone, scheme });
      setPoem(p => ({ ...p, text: data.text, rhyme_score: data.rhyme_score, regenerations_used: data.regenerations_used }));
      setDraft(data.text);
      toast.success("Re-rolled with a fresh poem.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed.");
    } finally {
      setRegenerating(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/poem/${poem_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `poem-${(poem?.deceased_name || "memorial").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return <div style={{ padding: 80, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>;
  if (!poem) return (
    <div style={{ padding: 80, textAlign: "center" }} data-testid="poem-not-found">
      <h2 className="serif" style={{ fontSize: 28, color: "#fff" }}>Poem not found</h2>
      <p className="muted" style={{ marginTop: 12 }}>This poem doesn&apos;t exist or doesn&apos;t belong to your account.</p>
      <Link to="/dashboard" className="btn-gold" style={{ marginTop: 18, display: "inline-block", padding: "10px 18px", textDecoration: "none" }}>Back to library</Link>
    </div>
  );

  const rhymeColor = poem.rhyme_score >= 0.6 ? "#7fd07a" : poem.rhyme_score >= 0.4 ? "#d4af37" : "#e8a07a";
  const regenUsed = poem.regenerations_used || 0;
  const regenLeft = 5 - regenUsed;

  return (
    <div style={{ maxWidth: 820, margin: "60px auto", padding: "0 24px" }} data-testid="poem-page">
      <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>
        ✓ UNLOCKED · RHYMING MEMORIAL POEM
      </div>
      <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: "0 0 8px", fontWeight: 400 }}>
        A poem for {poem.deceased_name}
      </h1>
      <p className="muted" style={{ fontSize: 14, marginBottom: 22 }}>
        Edit any line below. Download the watermark-free PDF anytime. You have {regenLeft} re-roll{regenLeft === 1 ? "" : "s"} left.
      </p>

      <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ padding: "8px 14px", background: "#0f0f10", border: "1px solid #2a2a2a", borderRadius: 999, fontSize: 12, color: "#bbb" }}>
          Tone: <b style={{ color: "#fff" }}>{poem.tone}</b>
        </div>
        <div style={{ padding: "8px 14px", background: "#0f0f10", border: "1px solid #2a2a2a", borderRadius: 999, fontSize: 12, color: "#bbb" }}>
          Scheme: <b style={{ color: "#fff" }}>{poem.scheme}</b>
        </div>
        <div style={{ padding: "8px 14px", background: "#0f0f10", border: "1px solid #2a2a2a", borderRadius: 999, fontSize: 12, color: "#bbb" }}>
          Rhyme score: <b style={{ color: rhymeColor }}>{poem.rhyme_score?.toFixed?.(2) ?? "—"}</b>
        </div>
      </div>

      <textarea
        data-testid="poem-editor"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        rows={20}
        style={{
          width: "100%", padding: 24,
          background: "#0f0f10", border: "1px solid #2a2a2a", borderRadius: 12,
          color: "#fff", fontFamily: "Georgia, serif", fontSize: 17, lineHeight: 1.85,
          resize: "vertical", textAlign: "center",
        }}
      />
      <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
        Tip: Keep each line ~8-10 syllables. The rhyme score auto-updates when you save.
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
        <button data-testid="poem-save-btn" onClick={save} disabled={saving} className="btn-ghost" style={{ padding: "12px 22px" }}>
          {saving ? "Saving…" : "Save edits"}
        </button>
        <button data-testid="poem-regenerate-btn" onClick={regenerate} disabled={regenerating || regenLeft <= 0} className="btn-ghost" style={{ padding: "12px 22px" }}>
          {regenerating ? "Re-rolling…" : (regenLeft <= 0 ? "No re-rolls left" : `Re-roll (${regenLeft} left)`)}
        </button>
        <button data-testid="poem-download-pdf-btn" onClick={downloadPdf} disabled={pdfLoading} className="btn-gold" style={{ padding: "12px 22px" }}>
          {pdfLoading ? "Building PDF…" : "Download PDF"}
        </button>
        {poem.obituary_id && (
          <Link to={`/obituary/${poem.obituary_id}`} className="btn-ghost" data-testid="poem-back-link" style={{ padding: "12px 22px", textDecoration: "none" }}>
            Back to obituary
          </Link>
        )}
      </div>
    </div>
  );
}
